import React, { useEffect, useMemo, useState, useCallback, memo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CalendarDays, BookOpen, Award, PlayCircle, Sparkles, Clock,
  Users, User, Timer, ListChecks
} from "lucide-react";
import {
  fetchAppMe,
  fetchLearnerOverview,
  getLearnerCourses,
  getLearnerUpcoming,
  getLearnerBadges,
  startLearnerRun
} from "@/api/kuizy";
import { useKuizyTheme } from "@/styles/kuizyTheme";
import LearnerLayout from "@/layout/LearnerLayout";

const fadeIn = { initial: { opacity: 0, y: 18 }, animate: { opacity: 1, y: 0 }, transition: { duration: .28 } };
const pop = { initial: { opacity: 0, scale: .96 }, animate: { opacity: 1, scale: 1 }, transition: { duration: .22 } };

function useDebouncedValue(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

const Dot = ({ className = "" }) => <span className={`inline-block w-3 h-3 rounded-full ${className}`} />;

export default function LearnerHomePage() {
  const nav = useNavigate();
  const { t, theme } = useKuizyTheme();

  const [meLoading, setMeLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [coursesActive, setCoursesActive] = useState([]);
  const [coursesHistory, setCoursesHistory] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [q, setQ] = useState("");
  const dq = useDebouncedValue(q, 280);

  useEffect(() => {
    localStorage.setItem("viewMode", "user");
  }, []);

  useEffect(() => {
    let ignore = false;
    (async () => {
      try { await fetchAppMe(); if (ignore) return; }
      catch { if (!ignore) nav("/learn/login", { replace: true }); }
      finally { if (!ignore) setMeLoading(false); }
    })();
    return () => { ignore = true; };
  }, [nav]);

  useEffect(() => {
    let ignore = false;
    if (meLoading) return;
    (async () => {
      setLoading(true);
      try {
        const [ov, ac, hi, up, bd] = await Promise.all([
          fetchLearnerOverview().catch(() => ({ data: {} })),
          getLearnerCourses({ status: "active" }).catch(() => ({ data: [] })),
          getLearnerCourses({ status: "history" }).catch(() => ({ data: [] })),
          getLearnerUpcoming().catch(() => ({ data: [] })),
          getLearnerBadges().catch(() => ({ data: [] }))
        ]);
        if (ignore) return;
        const ovData = ov?.data || {};
        setProfile(ovData.profile || {});
        setStats(ovData.stats || {});
        setCoursesActive(Array.isArray(ac?.data?.rows) ? ac.data.rows : (ac?.data || []));
        setCoursesHistory(Array.isArray(hi?.data?.rows) ? hi.data.rows : (hi?.data || []));
        const upRows = Array.isArray(up?.data?.rows) ? [...up.data.rows] : ([...(up?.data || [])]);
        upRows.sort((a, b) => {
          const ca = String(a?.course_id || "");
          const cb = String(b?.course_id || "");
          if (ca !== cb) return ca.localeCompare(cb);
          const ta = a?.test_order == null ? Number.MAX_SAFE_INTEGER : Number(a.test_order);
          const tb = b?.test_order == null ? Number.MAX_SAFE_INTEGER : Number(b.test_order);
          if (ta !== tb) return ta - tb;
          const aa = String(a?.test_id || "");
          const bb = String(b?.test_id || "");
          return aa.localeCompare(bb);
        });
        setUpcoming(upRows);
        setBadges(Array.isArray(bd?.data?.rows) ? bd.data.rows : (bd?.data || []));
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, [meLoading]);

  useEffect(() => { document.title = "ผู้เรียน • Kuizy"; }, []);

  const overviewMapped = useMemo(() => ({
    active_courses: stats?.total_courses ?? 0,
    avg_score: stats?.avg_score ?? 0,
  }), [stats]);

  const filteredActive = useMemo(() => {
    if (!dq) return coursesActive;
    const s = String(dq).toLowerCase();
    return coursesActive.filter(v => (v?.name || v?.course_name || "").toLowerCase().includes(s));
  }, [dq, coursesActive]);

  const filteredHistory = useMemo(() => {
    if (!dq) return coursesHistory;
    const s = String(dq).toLowerCase();
    return coursesHistory.filter(v => (v?.name || v?.course_name || "").toLowerCase().includes(s));
  }, [dq, coursesHistory]);

  const refreshMe = useCallback(async () => {
    const ov = await fetchLearnerOverview().catch(() => ({ data: {} }));
    const d = ov?.data || {};
    setProfile(d.profile || {});
    setStats(d.stats || {});
  }, []);

  const setModeThenNavigate = useCallback((mode, to) => {
    localStorage.setItem("viewMode", mode);
    window.location.replace(to);
  }, []);


  if (meLoading) {
    return (
      <div className={`min-h-screen grid place-items-center ${t.bg}`}>
        <div className="animate-pulse">
          <div className="h-12 w-12 rounded-2xl bg-primary/30" />
        </div>
      </div>
    );
  }

  const tabBase = "h-8 px-3 rounded-md text-xs font-medium transition";
  const tabInactive = theme === "dark" ? "text-white/85 hover:bg-white/10" : "text-gray-700 hover:bg-black/5";
  const tabSwitch = (
    <div className={`flex items-center rounded-lg p-0.5 ${theme === "dark" ? "bg-white/10" : "bg-gray-100"}`}>
      <button onClick={() => setTab("active")} className={`${tabBase} ${tab === "active" ? "bg-primary text-white shadow" : tabInactive}`}>กำลังเรียน</button>
      <button onClick={() => setTab("history")} className={`${tabBase} ${tab === "history" ? "bg-secondary text-white shadow" : tabInactive}`}>ประวัติ</button>
    </div>
  );

  return (
    <LearnerLayout profile={profile} onProfileSaved={refreshMe}>

      <main className="max-w-6xl mx-auto px-4 pb-12 pt-4">
        <motion.div {...pop} className={`rounded-2xl p-5 ${t.card}`}>
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="h-8 w-8 rounded-lg grid place-items-center bg-gradient-to-br from-primary to-secondary text-white">
                <Sparkles className="size-4" />
              </span>
              <div className={`text-base font-semibold ${t.text}`}>ค้นหาและประวัติ</div>
            </div>
            <div className="hidden sm:block">{tabSwitch}</div>
          </div>
          <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_auto]">
            <div className={`flex items-center gap-2 h-11 px-3 rounded-xl ${t.input}`}>
              <input
                value={q}
                onChange={e => setQ(e.target.value)}
                placeholder="ค้นหาหลักสูตรของฉัน"
                className="bg-transparent outline-none text-sm w-full"
              />
            </div>
            <div className="sm:hidden">{tabSwitch}</div>
          </div>
          <div className={`text-sm mt-3 ${t.subtext}`}>
            คอร์สทั้งหมด {stats?.total_courses ?? 0} • ผ่านแล้ว {stats?.completed_courses ?? 0} • ส่งข้อสอบ {stats?.total_submissions ?? 0} ครั้ง • คะแนนเฉลี่ย {stats?.avg_score ? `${Math.round(stats.avg_score)}%` : "-"}
          </div>
        </motion.div>

        {/* Course Section — full width */}
        <div className="mt-6">
          <CourseSection
            t={t}
            tab={tab}
            loading={loading}
            active={filteredActive}
            history={filteredHistory}
            isDark={theme === "dark"}
          />
        </div>

        {/* Upcoming + Certificate — side by side */}
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <UpcomingSection
            t={t}
            loading={loading}
            items={upcoming}
            isDark={theme === "dark"}
          />
          <CertificateSection
            t={t}
            loading={loading}
            items={badges}
          />
        </div>

        <section className="mt-8">
          <div className="grid gap-3 sm:grid-cols-2">
            <motion.div {...pop} className={`rounded-2xl p-5 ${t.card}`}>
              <div className="flex items-center gap-2">
                <span className="h-8 w-8 rounded-lg grid place-items-center bg-gradient-to-br from-primary to-secondary text-white">
                  <Sparkles className="size-4" />
                </span>
                <div className={`text-base font-semibold ${t.text}`}>สรุปความคืบหน้า</div>
              </div>
              <div className={`text-sm mt-2 ${t.subtext}`}>
                คอร์สทั้งหมด {stats?.total_courses ?? 0} • ผ่านแล้ว {stats?.completed_courses ?? 0} • ส่งข้อสอบ {stats?.total_submissions ?? 0} ครั้ง
              </div>
            </motion.div>
            <motion.div {...pop} className={`rounded-2xl p-5 ${t.card}`}>
              <div className="flex items-center gap-2">
                <span className="h-8 w-8 rounded-lg grid place-items-center bg-gradient-to-br from-secondary to-primary text-white">
                  <Clock className="size-4" />
                </span>
                <div className={`text-base font-semibold ${t.text}`}>คะแนนเฉลี่ย</div>
              </div>
              <div className={`text-2xl font-semibold mt-2 ${t.text}`}>
                {stats?.avg_score ? `${Math.round(stats.avg_score)}%` : "-"}
              </div>
              <div className={`text-sm mt-1 ${t.subtext}`}>รักษาความสม่ำเสมอแล้วผลลัพธ์จะดีขึ้น</div>
            </motion.div>
          </div>
        </section>
      </main>

    </LearnerLayout>
  );
}



const CourseSection = memo(function CourseSection({ t, tab, loading, active, history, isDark }) {
  const items = tab === "active" ? active : history;
  return (
    <motion.div {...fadeIn} className={`rounded-2xl p-5 ${t.card}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <BookOpen className="size-5 text-primary" />
          <span className={`text-base font-semibold ${t.text}`}>{tab === "active" ? "หลักสูตรที่กำลังเรียน" : "หลักสูตรที่ผ่านมา"}</span>
        </div>
        <Link to="/learn/courses" className={`text-sm ${t.subtext} hover:underline`}>ดูทั้งหมด</Link>
      </div>
      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="rounded-2xl overflow-hidden animate-pulse">
              <div className="h-40 bg-gray-200" />
              <div className="p-4 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length ? (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {items.map(it => {
            const img = it?.image_url || it?.logo_url || it?.thumbnail_url || it?.cover_url;
            return (
              <li key={it.id || `${it.course_id || ""}-${it.code || ""}`}
                className={`group rounded-2xl overflow-hidden border transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-white shadow-sm"}`}
              >
                {/* Card Image */}
                <div className="relative h-40 bg-gradient-to-br from-primary/20 to-secondary/20 overflow-hidden">
                  {img ? (
                    <img src={img} alt={it?.name || it?.course_name} className="w-full h-full group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <BookOpen className="size-12 text-primary/30" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
                  {/* {(it?.code || it?.course_code) && (
                    <span className="absolute top-3 left-3 text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-black/50 text-white backdrop-blur-sm">
                      {it?.code || it?.course_code}
                    </span>
                  )} */}
                  {/* Course name overlay on image bottom */}
                  {/* <div className="absolute bottom-0 left-0 right-0 p-3">
                    <div className="font-semibold text-sm text-white leading-tight line-clamp-2">
                      {it?.name || it?.course_name}
                    </div>
                  </div> */}
                </div>

                {/* Card Body */}
                <div className="p-3 flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1 space-y-0.5">
                    {/* {(it?.classroom_name || it?.class_name || it?.room_name || it?.group_name) && (
                      <div className="flex items-center gap-1 text-xs">
                        <Users className="size-3 opacity-50 shrink-0" />
                        <span className={`${t.subtext} truncate`}>
                          {it.classroom_name || it.class_name || it.room_name || it.group_name}
                        </span>
                      </div>
                    )}
                    {it?.instructor_name && (
                      <div className="flex items-center gap-1 text-xs">
                        <User className="size-3 opacity-50 shrink-0" />
                        <span className={`${t.subtext} truncate`}>{it.instructor_name}</span>
                      </div>
                    )} */}
                    {!it?.classroom_name && !it?.class_name && !it?.instructor_name && (
                      <div className={`text-xs ${t.subtext}`}>{it.name || it.course_name}</div>
                    )}
                  </div>
                  <Link
                    to={`/learn/course/${it?.course_id || it?.id}`}
                    className="shrink-0 text-xs px-4 py-2 rounded-xl bg-primary text-white shadow-sm hover:opacity-90 transition-opacity font-semibold"
                  >
                    เข้าเรียน
                  </Link>
                </div>
              </li>
            );
          })}
        </ul>
      ) : <div className={`text-sm ${t.subtext}`}>ไม่มีข้อมูล</div>}
    </motion.div>
  );
});

const UpcomingSection = memo(function UpcomingSection({ t, loading, items, isDark }) {
  const nav = useNavigate();
  const readyChip = isDark ? "bg-green-500/20 text-green-300 ring-1 ring-green-500/30" : "bg-green-100 text-green-700 ring-1 ring-green-200";
  const soonChip = isDark ? "bg-orange-500/20 text-orange-300 ring-1 ring-orange-500/30" : "bg-orange-100 text-orange-700 ring-1 ring-orange-200";
  const overChip = isDark ? "bg-gray-500/20 text-gray-300 ring-1 ring-gray-500/30" : "bg-gray-100 text-gray-700 ring-1 ring-gray-200";
  const scoreChip = isDark ? "bg-blue-500/20 text-blue-300 ring-1 ring-blue-500/30" : "bg-blue-100 text-blue-700 ring-1 ring-blue-200";

  return (
    <motion.div {...fadeIn} className={`rounded-2xl p-5 ${t.card}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <CalendarDays className="size-5 text-secondary" />
          <span className={`text-base font-semibold ${t.text}`}>กำหนดการ/ข้อสอบใกล้ถึง</span>
        </div>
        <Link to="/learn/calendar" className={`text-sm ${t.subtext} hover:underline`}>ดูทั้งหมด</Link>
      </div>

      {loading ? (
        <div className="grid gap-4 sm:grid-cols-2">
          {[1, 2].map(i => (
            <div key={i} className="rounded-2xl overflow-hidden animate-pulse">
              <div className="h-36 bg-gray-200" />
              <div className="p-3 space-y-2">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-100 rounded w-1/2" />
                <div className="h-3 bg-gray-100 rounded w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : items.length ? (
        <ul className="grid gap-4 sm:grid-cols-2">
          {items.map(it => {
            const st = it?.start_time ? new Date(it.start_time) : null;
            const et = it?.end_time ? new Date(it.end_time) : null;
            const meta = getStatusMeta(st, et);
            const chipCls = meta.kind === "ready" ? readyChip : meta.kind === "soon" ? soonChip : overChip;
            const qn = it?.question_count ?? it?.total_questions ?? it?.num_questions;
            const dur = it?.duration_minutes ?? it?.duration ?? it?.limit_min;
            const teacher = it?.instructor_name ?? it?.teacher_name ?? it?.teacher_fullname ?? it?.trainer_name ?? it?.lecturer_name ?? it?.owner_name ?? it?.created_by_name ?? it?.owner ?? "";
            const klass = it?.classroom_name ?? it?.class_name ?? it?.room_name ?? it?.section_name ?? it?.group_name ?? it?.course_section ?? "";
            const img = it?.image_url || it?.logo_url || it?.thumbnail_url || it?.cover_url;

            const handleStart = async (courseId, testId) => {
              try {
                const { data } = await startLearnerRun({ course_id: courseId, test_id: testId });
                const rid = data?.run_id || data?.id || data?.test_run_id;
                if (rid) nav(`/learn/exam/${rid}`);
                else nav(`/learn/exam?course_id=${courseId}&test_id=${testId}`);
              } catch { }
            };

            const alreadySubmitted = !!it?.submission_id;
            const scoreText = (it?.score != null && it?.full_score != null) ? `คะแนน ${it.score}/${it.full_score}` : null;

            return (
              <li
                key={it.course_test_id || `${it.course_id}-${it.test_id}-${it.test_order || ""}`}
                className={`group rounded-2xl overflow-hidden border transition-all duration-200 hover:shadow-xl hover:-translate-y-0.5 ${isDark ? "border-white/10 bg-white/5" : "border-gray-100 bg-white shadow-sm"}`}
              >
                {/* Card Image */}
                <div className="relative h-36 overflow-hidden bg-gradient-to-br from-secondary/30 to-primary/20">
                  {img ? (
                    <img
                      src={img}
                      alt={it?.test_name || it?.course_name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center opacity-60">
                      <CalendarDays className="size-10 text-secondary" />
                    </div>
                  )}
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  {/* Status chip top-right */}
                  <span className={`absolute top-2.5 right-2.5 text-[11px] px-2 py-0.5 rounded-full font-medium backdrop-blur-sm ${chipCls}`}>
                    {meta.text}
                  </span>
                  {/* Test name overlay */}
                  <div className="absolute bottom-0 left-0 right-0 px-3 pb-2.5 pt-6">
                    <div className="font-semibold text-sm text-white leading-tight line-clamp-2 drop-shadow">
                      {it?.test_name}
                    </div>
                  </div>
                </div>

                {/* Card Body */}
                <div className="p-3 space-y-2">
                  <div className={`text-xs font-medium truncate ${t.subtext}`}>{it?.course_name}</div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                    {klass ? (
                      <div className="flex items-center gap-1 text-xs">
                        <Users className="size-3 opacity-60 shrink-0" />
                        <span className={`${t.subtext} truncate max-w-[120px]`}>{klass}</span>
                      </div>
                    ) : null}
                    {teacher ? (
                      <div className="flex items-center gap-1 text-xs">
                        <User className="size-3 opacity-60 shrink-0" />
                        <span className={`${t.subtext} truncate max-w-[120px]`}>{teacher}</span>
                      </div>
                    ) : null}
                    {qn ? (
                      <span className={`inline-flex items-center gap-1 text-xs ${t.subtext}`}>
                        <ListChecks className="size-3" />{qn} ข้อ
                      </span>
                    ) : null}
                    {dur ? (
                      <span className={`inline-flex items-center gap-1 text-xs ${t.subtext}`}>
                        <Timer className="size-3" />{dur} นาที
                      </span>
                    ) : null}
                  </div>
                  {(st || et) ? (
                    <div className={`text-xs ${t.subtext} flex items-center gap-1`}>
                      <Clock className="size-3 opacity-60 shrink-0" />
                      {fmtTimeRange(st, et)}
                    </div>
                  ) : null}
                  <div className="flex items-center gap-2 pt-1">
                    {!alreadySubmitted && meta.kind === "ready" ? (
                      <button
                        onClick={() => handleStart(it.course_id, it.test_id)}
                        className="flex-1 h-8 rounded-xl text-xs font-semibold bg-primary text-white shadow-sm hover:opacity-90 transition-opacity cursor-pointer"
                      >
                        เริ่มสอบ
                      </button>
                    ) : null}
                    {alreadySubmitted && scoreText ? (
                      <button
                        className={`flex-1 h-8 rounded-xl text-xs font-semibold cursor-pointer transition-opacity hover:opacity-80 ${scoreChip}`}
                        onClick={() => {
                          const qs = new URLSearchParams({
                            sid: String(it.submission_id || ""),
                            score: String(it.score ?? 0),
                            full: String(it.full_score ?? 0),
                            spend: String(it.spend_sec ?? it.spend ?? 0)
                          }).toString();
                          nav(`/learn/exam/result/${it.test_run_id}?${qs}`);
                        }}
                      >
                        {scoreText}
                      </button>
                    ) : null}
                    {!alreadySubmitted && meta.kind !== "ready" ? (
                      <div className={`flex-1 h-8 rounded-xl text-xs font-semibold flex items-center justify-center ${overChip}`}>
                        {meta.kind === "soon" ? "ยังไม่ถึงเวลา" : "หมดเวลา"}
                      </div>
                    ) : null}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      ) : <div className={`text-sm ${t.subtext}`}>ไม่มีข้อมูล</div>}
    </motion.div>
  );
});

const CertificateSection = memo(function CertificateSection({ t, loading, items }) {
  return (
    <motion.div {...fadeIn} className={`rounded-2xl p-5 ${t.card}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Award className="size-5 text-yellow-500" />
          <span className={`text-base font-semibold ${t.text}`}>ประกาศนียบัตร</span>
        </div>
        <Link to="/learn/rewards" className={`text-sm ${t.subtext} hover:underline`}>ดูทั้งหมด</Link>
      </div>
      {loading ? <div className="h-24 animate-pulse bg-gray-200 rounded-xl" /> : (
        items.length ? (
          <ul className="space-y-3">
            {items.map((it, idx) => (
              <li key={idx} className="flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r from-yellow-100/30 to-yellow-50/10">
                <Award className="size-6 text-yellow-600" />
                <div>
                  <div className={`font-medium ${t.text}`}>{it?.name}</div>
                  <div className={`text-xs ${t.subtext}`}>{it?.points} คะแนน</div>
                </div>
              </li>
            ))}
          </ul>
        ) : <div className={`text-sm ${t.subtext}`}>ยังไม่มีประกาศนียบัตร</div>
      )}
    </motion.div>
  );
});

function fmtTimeRange(st, et) {
  try {
    if (!st && !et) return "";
    const a = st instanceof Date ? st : new Date(st);
    const b = et instanceof Date ? et : new Date(et);
    if (a && b) return `${a.toLocaleString()} - ${b.toLocaleString()}`;
    return (a ? a.toLocaleString() : "") || (b ? b.toLocaleString() : "");
  } catch { return ""; }
}

function getStatusMeta(st, et) {
  const now = Date.now();
  const s = st ? (st instanceof Date ? st : new Date(st)).getTime() : null;
  const e = et ? (et instanceof Date ? et : new Date(et)).getTime() : null;
  if (s && now < s) {
    const diff = s - now;
    const m = Math.max(0, Math.floor(diff / 60000));
    const d = Math.floor(m / 1440);
    const h = Math.floor((m % 1440) / 60);
    const mm = m % 60;
    if (d > 0) return { text: `อีก ${d} วัน ${h} ชม`, kind: "soon" };
    if (h > 0) return { text: `อีก ${h} ชม ${mm} นาที`, kind: "soon" };
    return { text: `อีก ${mm} นาที`, kind: "soon" };
  }
  const started = !s || now >= s;
  const notEnded = !e || now <= e;
  if (started && notEnded) return { text: "พร้อม", kind: "ready" };
  return { text: "หมดเวลา", kind: "over" };
}
